import { create } from 'zustand';
import type { User, Settings, Task, LLMJobProgress } from '../types';
import { authApi, tasksApi } from '../services/api';

interface AppState {
  // Auth state
  user: User | null;
  settings: Settings | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Tasks state
  tasks: Task[];
  tasksLoading: boolean;
  hasNotionDatabase: boolean;
  
  // LLM job tracking
  activeJobs: Map<string, LLMJobProgress>;
  
  // Actions
  initialize: () => Promise<void>;
  logout: () => Promise<void>;
  
  loadTasks: () => Promise<void>;
  addTask: (task: Task) => void;
  updateTask: (id: string, updates: Partial<Task>) => void;
  removeTask: (id: string) => void;
  
  setJobProgress: (jobId: string, progress: LLMJobProgress) => void;
  removeJob: (jobId: string) => void;
  
  setSettings: (settings: Settings) => void;
  setUser: (user: User) => void;
}

export const useAppStore = create<AppState>((set, get) => ({
  // Initial state
  user: null,
  settings: null,
  isAuthenticated: false,
  isLoading: true,
  
  tasks: [],
  tasksLoading: false,
  hasNotionDatabase: false,
  
  activeJobs: new Map(),
  
  // Initialize app - check auth status
  initialize: async () => {
    try {
      const response = await authApi.getCurrentUser();
      if (response.success && response.data) {
        set({
          user: response.data.user,
          settings: response.data.settings,
          isAuthenticated: true,
          isLoading: false,
        });
        
        // Load tasks if user has a database configured
        if (response.data.user.notionTaskDatabaseId) {
          get().loadTasks();
        }
      } else {
        set({ isAuthenticated: false, isLoading: false });
      }
    } catch {
      set({ isAuthenticated: false, isLoading: false });
    }
  },
  
  logout: async () => {
    try {
      await authApi.logout();
    } finally {
      set({
        user: null,
        settings: null,
        isAuthenticated: false,
        tasks: [],
        activeJobs: new Map(),
      });
    }
  },
  
  loadTasks: async () => {
    set({ tasksLoading: true });
    try {
      const response = await tasksApi.list();
      if (response.success && response.data) {
        set({
          tasks: response.data.tasks,
          hasNotionDatabase: response.data.hasDatabase,
          tasksLoading: false,
        });
      }
    } catch (error) {
      console.error('Failed to load tasks:', error);
      set({ tasksLoading: false });
    }
  },
  
  addTask: (task) => {
    set((state) => ({
      tasks: [task, ...state.tasks],
    }));
  },
  
  updateTask: (id, updates) => {
    set((state) => ({
      tasks: state.tasks.map((task) =>
        task.id === id ? { ...task, ...updates } : task
      ),
    }));
  },
  
  removeTask: (id) => {
    set((state) => ({
      tasks: state.tasks.filter((task) => task.id !== id),
    }));
  },
  
  setJobProgress: (jobId, progress) => {
    set((state) => {
      const newJobs = new Map(state.activeJobs);
      newJobs.set(jobId, progress);
      return { activeJobs: newJobs };
    });
  },
  
  removeJob: (jobId) => {
    set((state) => {
      const newJobs = new Map(state.activeJobs);
      newJobs.delete(jobId);
      return { activeJobs: newJobs };
    });
  },
  
  setSettings: (settings) => {
    set({ settings });
  },
  
  setUser: (user) => {
    set({ user });
  },
}));

